import { DragEndEvent } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { PhotoShootData } from 'apis/mips/types';

import { getMenuItemImageIds, getReorderedImageIds } from './utils';
import { PhotoShootQueryKeys } from '../../constants/queryKeys';
import { useLinkImageMutation } from '../../hooks/useLinkImageMutation';
import { isLinkImageEvent, isReorderEvent } from '../../utils/typeGuards';

type UseDragEndHandlerProps = {
  venueId: string;
  token: string | null;
};

export const useDragEndHandler = ({ venueId, token }: UseDragEndHandlerProps) => {
  const queryClient = useQueryClient();
  const { mutate: handleLinkImage } = useLinkImageMutation({ venueId, token });

  return useCallback(
    (event: DragEndEvent) => {
      if (isReorderEvent(event)) {
        const menuItemId = event.over.data.current.id;
        const reorderedImageIds = getReorderedImageIds(event);

        queryClient.setQueryData<PhotoShootData>(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token), prevData =>
          !prevData
            ? undefined
            : {
                ...prevData,
                items: prevData?.items.map(item =>
                  item.id === menuItemId ? { ...item, imageIds: reorderedImageIds } : item,
                ),
              },
        );

        return handleLinkImage({ itemId: menuItemId, imageIds: reorderedImageIds });
      }

      if (isLinkImageEvent(event)) {
        const imageDraggableData = event.active.data.current;
        const droppableImageSlotData = event.over.data.current;

        const alreadyHasThisImage = droppableImageSlotData.images.some(image => image.id === imageDraggableData.id);
        if (alreadyHasThisImage) {
          toast.error(<FormattedMessage id="photo-shoot.duplicated-image-error" />);
          return;
        }

        const imageIds = getMenuItemImageIds({
          currentImages: droppableImageSlotData.images,
          newImageId: imageDraggableData.id,
          newImageSlotIndex: droppableImageSlotData.imageSlotIndex,
        });

        return handleLinkImage({ itemId: droppableImageSlotData.id, imageIds });
      }
    },
    [handleLinkImage, queryClient, token, venueId],
  );
};
